/**
 * Print resolution limits — ONE source of truth for every upload path.
 *
 * These numbers used to be copy-pasted as `MAX_PHOTO_DIM = 3600` into
 * AlbumFlowPage, AlbumEditorPage and MagicUploadPage (as `TARGET_DIM`). They
 * drifted before — migration 015 still describes the thumbnail tier as "~512px"
 * while the code writes 1600 — so they live here now and are imported.
 *
 * WHY 4800 AND NOT "the phone's maximum":
 * customers send photos from everything — iPhone (4032 px), Samsung, GoPro
 * (5568 px), Canon and other DSLRs (6000 px+). Sizing the cap to any single
 * device throws away real detail from the bigger ones. So the cap is set by
 * what the PRINTED PAGE can actually use:
 *
 *   interior page long edge = 297 mm = 11.693 in  (858.9 pt / 72)
 *     300 dpi (photo-print standard) → 3508 px
 *     400 dpi (beyond visible gain)  → 4677 px
 *
 * 4800 px clears 400 dpi on a full page with room left for a customer who
 * zooms into a crop (computeSlotTargetLongEdge multiplies by their zoom). Going
 * higher only inflates bytes and browser memory: the printer images at
 * 3600 × 2400 dpi of LASER DOTS, which is a different unit — feeding it 3600
 * dpi of PHOTO data would need a ~42,900 px image, which no camera produces.
 *
 * Anything already smaller is never enlarged (`Math.min(1, cap/longEdge)`), so
 * a WhatsApp-compressed 1600 px photo is stored as-is and simply prints soft —
 * raising this cap cannot invent pixels that were never sent.
 */

/** Long-edge cap (px) for the stored PRINT original. Aspect always preserved. */
export const PRINT_MAX_DIM = 4800;

/** JPEG quality for the stored print original. */
export const PRINT_JPEG_QUALITY = 0.88;

/** Long-edge cap (px) for the display/editor tier. Screen-only, never printed. */
export const DISPLAY_MAX_DIM = 1600;

/** JPEG quality for the display tier. */
export const DISPLAY_JPEG_QUALITY = 0.82;

/**
 * Long-edge cap (px) for the photo-TRAY tile. Screen-only, never printed.
 *
 * The tray, the slot picker and the mobile "Mes photos" sheet draw ~80px
 * tiles. 320px covers a 3x device pixel ratio with room to spare, and it is
 * the size the billed image-transform used to return — so tiles look exactly
 * as they do today, while costing nothing (migration 128, lib/photoTiers.ts).
 */
export const TILE_MAX_DIM = 320;

/** JPEG quality for the tray tile — matches the old transform's quality 60. */
export const TILE_JPEG_QUALITY = 0.6;
